/* eslint-disable react-hooks/exhaustive-deps */
import { Link } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { BuildDate } from "../../component/Functions";
import { SearchMatchingAdmins } from "./SearchFunction";
import { _PAGE_SIZE_ } from "../../../../middlewares/other/tools";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { pushOneAdmin } from "../../../../middlewares/sysNavigation/SysNavigation";
import { useAdminsContext } from "../../../../context/admin/context/AdminManageContext";
import { Modal, MyButton, MySearch, Pagination } from "../../component/Component";
import {
  BsPeople,
  BsPlusLg,
  BsPerson,
  BsCalendarEvent,
  BsGear,
  BsPencil,
  BsTrash,
} from "react-icons/bs";

const addRoute = "/admin/add";
const editRoute = "/admin/edit";

const Overview = () => {
  const { admins, getAllByAdminCompanyId, count } = useAdminsContext();
  const [page, setPage] = useState(1);
  const [adminsList, setAdminsList] = useState(null);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  useEffect(() => {
    if (!admins) getAllByAdminCompanyId(page, pageSize);
    else setAdminsList(admins);
  }, [admins]);

  const totalAdmins = useMemo(
    () => (typeof count === "number" ? count : adminsList?.length || 0),
    [count, adminsList]
  );

  const handleSearch = (val) => {
    const key = typeof val === "string" ? val : val?.target?.value || "";
    setAdminsList(SearchMatchingAdmins(key, admins));
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      {/* Header */}
      <div className="relative mb-4">
        <div className="absolute -inset-x-3 -top-2 h-16 rounded-3xl bg-gradient-to-r from-emerald-400/20 via-amber-400/20 to-fuchsia-400/20 blur-2xl" />
        <div className="relative rounded-3xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
          <header className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white shadow-sm">
                <BsPeople />
              </div>
              <div>
                <h1 className="text-base font-semibold tracking-tight text-gray-900">
                  Admins
                </h1>
                <p className="mt-0.5 text-xs text-gray-600">
                  Manage your organization administrators with quick search and streamlined actions.
                </p>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center">
              {/* Search — keep it light (no extra icons/rings here) */}
              <div className="w-full lg:w-80">
                <label className="sr-only" htmlFor="admin-search">Search admins</label>
                <MySearch id="admin-search" onChange={handleSearch} />
              </div>

              {/* Add Admin + Stats */}
              <div className="flex items-center justify-between gap-3 lg:justify-start">
                <StatChip icon={<BsPeople />} label="Total" value={totalAdmins} />
                <Link to={addRoute}>
                  <MyButton
                    title={
                      <span className="inline-flex items-center gap-2">
                        <BsPlusLg /> Add Admin
                      </span>
                    }
                    className="rounded-lg bg-gradient-to-br from-gray-900 to-gray-700 hover:from-black hover:to-gray-800 px-4 py-2.5 text-xs font-semibold shadow"
                    onClick={() => { }}
                  />
                </Link>
              </div>
            </div>
          </header>
        </div>
      </div>

      {/* Table Card */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="relative max-h-[62vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white/90 backdrop-blur">
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-semibold w-[26%]">
                  <span className="inline-flex items-center gap-2">
                    <BsPerson /> First Name
                  </span>
                </th>
                <th className="px-4 py-3 font-semibold w-[26%]">
                  <span className="inline-flex items-center gap-2">
                    <BsPerson /> Last Name
                  </span>
                </th>
                <th className="px-4 py-3 font-semibold w-[26%]">
                  <span className="inline-flex items-center gap-2">
                    <BsCalendarEvent /> Date Of Joining
                  </span>
                </th>
                <th className="px-4 py-3 font-semibold text-center w-[22%]">
                  <span className="inline-flex items-center justify-center gap-2">
                    <BsGear /> Manage
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {adminsList ? (
                adminsList.map((admin, idx) => {
                  const { first_name, last_name, createdAt } = admin;
                  const initials =
                    `${(first_name || "").slice(0, 1)}${(last_name || "").slice(0, 1)}`.toUpperCase() || "AD";

                  return (
                    <tr
                      key={`${admin?.admin_id || idx}`}
                      className="group border-t border-gray-100 even:bg-gray-50/40 hover:bg-gray-50/70"
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-3">
                          <div className="grid h-8 w-8 place-items-center rounded-lg bg-gray-900 text-white text-[11px] font-bold shadow-sm">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-gray-900">
                              {first_name || "-"}
                            </div>
                            <div className="truncate text-[11px] text-gray-500">
                              First name
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 align-middle">
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-gray-900">
                            {last_name || "-"}
                          </div>
                          <div className="truncate text-[11px] text-gray-500">
                            Last name
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 align-middle">
                        <span className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200">
                          {BuildDate({ ref: createdAt })}
                        </span>
                      </td>

                      <td className="px-4 py-3 align-middle">
                        <div className="flex justify-center">
                          <CallToAction admin={admin} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr className="border-t border-gray-100">
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={4}>
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Single pagination at bottom */}
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="flex items-center justify-end">
            <Pagination
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              total_items={count}
              setPageSize={setPageSize}
              callbackFunction={(p, size) => getAllByAdminCompanyId(p, size)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StatChip = ({ icon, label, value }) => (
  <div className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[11px] font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200">
    <span className="text-gray-500">{icon}</span>
    <span className="uppercase tracking-wide">{label}</span>
    <span className="ml-1 grid place-items-center rounded-md bg-gradient-to-br from-slate-900 to-slate-700 px-2 py-0.5 text-white">
      {typeof value === "number" ? value.toLocaleString() : value}
    </span>
  </div>
);

/**
 * @author Ntavigwa Bashombe
 * @param {*} admin
 * @returns JSX.Element
 */
const CallToAction = ({ admin }) => {
  const { admin_id, first_name, last_name } = admin;
  const { sysAdmin } = useAdminContext();
  const { admins, setAdmins, deleteAdmin } = useAdminsContext();

  const [showModal, setShowModal] = useState(false);

  const deleteById = async (id) => {
    const { status } = await deleteAdmin(id);
    if (status === 200 || status === 201) {
      setAdmins(admins?.filter((x) => x?.admin_id !== id) || null);
      setShowModal(false);
    }
  };

  return (
    <>
      <Modal
        props={{
          name: `${first_name} ${last_name}`,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteById(admin_id),
        }}
      />

      <div
        className={[
          "inline-flex w-full max-w-[420px] flex-wrap items-center justify-center gap-2",
          "rounded-xl border border-gray-200 bg-white/70 px-2.5 py-2 shadow-sm",
        ].join(" ")}
      >
        <Link to={editRoute}>
          <MyButton
            title={
              <span className="inline-flex items-center gap-2">
                <BsPencil /> Edit
              </span>
            }
            className="rounded-lg bg-gradient-to-br from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 px-3 py-2 text-[11px] font-semibold"
            onClick={() => pushOneAdmin(admin)}
          />
        </Link>
        <MyButton
          title={
            <span className="inline-flex items-center gap-2">
              <BsTrash /> Delete
            </span>
          }
          disabled={admin_id === sysAdmin?.admin_id}
          className="rounded-lg bg-gradient-to-br from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 disabled:from-red-300 disabled:to-red-300 px-3 py-2 text-[11px] font-semibold"
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;
